import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMap } from 'react-map-gl';
import { LineLayer } from 'mapbox-gl';
import { Form, Row } from '@components/form/Form/Form';
import { MapMarkerField } from '@components/form/MapMarkerField/MapMarkerField';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { getFilledFieldsObjOnFormSubmit, isErrorResponse } from '@utils/utils';
import { createMapBoxLayer } from '@utils/mapUtils';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName, selectSourceById } from '@redux/selectors/selectors';
import { usePresetsContext } from '@context/presetsContext';
import { useMapMarkersCoordinates } from '@hooks/useMapMarkersCoordinates';
import {
    cleanRoadRouteCheckPoints,
    ROAD_ROUTE_CHECKER_MARKER_NAME,
} from '@redux/road-route-check/road-route-check-reducer';
import { EntityServiceName } from '@api/enums/enums';
import { FormDataObj } from '@utils/types';

const newSourceId = 'route-check-highlight';

type RouteCheckerBaseProps<TDetails> = {
    title: string;
    submitBtnTitle: string;
    layerEntityName: string;
    entityServiceName: EntityServiceName;
    formFields: React.ReactNode;
    showHeaderSpacer?: boolean;
    getRouteDetails: (args: {
        entityServiceName: EntityServiceName;
        presetId: string;
        formDataObj: FormDataObj;
        abortSignal: AbortSignal;
    }) => Promise<TDetails | any>;
    getFeatureStateIds: (routeDetails: TDetails) => string[];
    onError: (error: any) => void;
    renderResult: (routeDetails: TDetails) => React.ReactNode;
};

const RouteCheckerBase = <TDetails,>({
    title,
    submitBtnTitle,
    layerEntityName,
    entityServiceName,
    formFields,
    showHeaderSpacer = false,
    getRouteDetails,
    getFeatureStateIds,
    onError,
    renderResult,
}: RouteCheckerBaseProps<TDetails>) => {
    const sourceId = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, layerEntityName)
    );
    const source = useTypedSelector(state => selectSourceById(state, sourceId));
    const [loading, setLoading] = useState<boolean>(false);
    const [routeDetails, setRouteDetails] = useState<TDetails | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const { selectedPresetId: presetId } = usePresetsContext();
    const { markersCoordinates } = useMapMarkersCoordinates({
        fromPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint,
        toPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.toPoint,
    });
    const dispatch = useDispatch();
    const { mapboxMapId } = useMap();

    useEffect(() => {
        return () => {
            dispatch(cleanRoadRouteCheckPoints());
        };
    }, [dispatch]);

    useEffect(() => {
        return () => {
            try {
                const map = mapboxMapId?.getMap();
                if (!map) {
                    return;
                }

                if (map.isStyleLoaded() && map.getLayer(newSourceId)) {
                    map.removeLayer(newSourceId);
                }
            } catch (_e) {
                console.error(
                    "Replan. Route check: for the moment it's not clear how to fix this issue, therefore it's intentionally wrapped by try catch"
                );
            }
        };
    }, [mapboxMapId]);

    const onFormSubmit = useCallback(
        async event => {
            const abortController = new AbortController();
            const formDataObj = getFilledFieldsObjOnFormSubmit(event);

            setLoading(true);
            const resp = await getRouteDetails({
                entityServiceName,
                presetId,
                formDataObj,
                abortSignal: abortController.signal,
            });

            if (isErrorResponse(resp)) {
                onError(resp);
            } else {
                setRouteDetails(resp);
                const featureStateIds = getFeatureStateIds(resp) ?? [];
                const map = mapboxMapId?.getMap();

                if (map?.getLayer(newSourceId)) {
                    map.removeLayer(newSourceId);
                }

                const mapBoxLayer = () => {
                    const layer = createMapBoxLayer(source);
                    return {
                        ...layer,
                        source: sourceId,
                        id: newSourceId,
                        layerName: newSourceId,
                        layout: {
                            ...layer.layout,
                            visibility: 'visible',
                        },
                        paint: {
                            ...layer.paint,
                            'line-color': 'red',
                        },
                        filter: ['in', ['get', 'featureStateId'], ['literal', featureStateIds]],
                    };
                };

                map?.addLayer(mapBoxLayer() as LineLayer);
            }

            setLoading(false);
        },
        [
            getRouteDetails,
            entityServiceName,
            presetId,
            onError,
            getFeatureStateIds,
            mapboxMapId,
            source,
            sourceId,
        ]
    );

    return (
        <div className="road-route-checker">
            <div className="road-route-checker__form">
                {showHeaderSpacer ? <div className="road-route-checker__header"></div> : null}
                <LoadingBackdrop isVisible={loading} transparent>
                    <div className="entity-editor__header">
                        <div className="title">{title}</div>
                    </div>
                    {markersCoordinates.fromPoint && markersCoordinates.toPoint ? (
                        <Form
                            name="entityForm"
                            onSubmit={onFormSubmit}
                            submitBtnTitle={submitBtnTitle}
                            ref={formRef}
                            submitBtnTestId="entityFormSubmitBtn"
                            testId="entityForm"
                        >
                            <Row>
                                <MapMarkerField
                                    name={ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint}
                                    defaultCoordinates={markersCoordinates.fromPoint}
                                    fieldGroupClassName="map-marker-form-field"
                                />
                                <MapMarkerField
                                    name={ROAD_ROUTE_CHECKER_MARKER_NAME.toPoint}
                                    defaultCoordinates={markersCoordinates.toPoint}
                                    fieldGroupClassName="map-marker-form-field"
                                />
                            </Row>
                            {formFields}
                        </Form>
                    ) : null}
                </LoadingBackdrop>
            </div>
            <div className="road-route-checker__result">
                {routeDetails ? renderResult(routeDetails) : null}
            </div>
        </div>
    );
};

export default RouteCheckerBase;
